import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase, BLOG_TABLE } from '@/lib/supabase';
import type { BlogRecord } from '@/lib/types';
import { formatDate } from '@/lib/format';
import Reveal from '@/components/feature/Reveal';

export default function BlogList() {
  const [items, setItems] = useState<BlogRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const { data, error: err } = await supabase
          .from(BLOG_TABLE)
          .select('*')
          .eq('is_published', true)
          .order('published_at', { ascending: false });
        if (err) throw err;
        if (active) setItems(data || []);
      } catch {
        if (active) setError('ブログの取得に失敗しました。');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [reload]);

  return (
    <section className="pt-32 md:pt-40 pb-20 md:pb-28 px-4 md:px-6 bg-[#0d0d1a]">
      <div className="max-w-6xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#1e1e3f] border border-[#3b3b6b]">
          <span className="w-2 h-2 rounded-full bg-[#a855f7]"></span>
          <span className="text-xs font-medium tracking-[0.22em] uppercase text-[#a78bfa]">Blog</span>
        </div>
        <h1 className="mt-5 text-3xl md:text-5xl font-extrabold text-white">ブログ</h1>
        <p className="mt-4 text-[#a0a0b0] text-base">AI活用に役立つナレッジやノウハウを発信しています。</p>

        {loading ? (
          <div className="mt-12 bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl p-16 text-center text-[#a0a0b0]">
            <i className="ri-loader-4-line animate-spin text-2xl"></i>
          </div>
        ) : error ? (
          <div className="mt-12 bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl p-12 text-center">
            <p className="text-[#e8e8f0]">{error}</p>
            <button
              type="button"
              onClick={() => setReload((r) => r + 1)}
              className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-xl h-10 px-5 text-sm font-medium bg-[#1a1a2e] border border-[#2a2a3e] text-[#e8e8f0] hover:bg-[#2a2a3e] transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              再読み込み
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="mt-12 bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl p-16 text-center">
            <i className="ri-book-open-line text-4xl text-[#7a7a8a]"></i>
            <p className="mt-4 text-[#e8e8f0]">記事を準備中です。</p>
          </div>
        ) : (
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((item, i) => (
              <Reveal key={item.id} delay={i * 60}>
                <Link
                  to={`/blog/${item.slug}/`}
                  className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl overflow-hidden flex flex-col h-full group transition-all duration-300 hover:-translate-y-1 hover:border-[#7c3aed] hover:shadow-[0_0_20px_rgba(124,58,237,0.2)] cursor-pointer"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={item.thumbnail_url || ''}
                      alt={item.title}
                      title={`${item.title} | 株式会社ルヴィラ`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3">
                      <span className="px-3 py-1 rounded-full bg-[#7c3aed]/15 text-[#a78bfa] text-xs font-medium whitespace-nowrap">
                        {item.category}
                      </span>
                      <span className="text-xs text-[#7a7a8a] whitespace-nowrap">{formatDate(item.published_at)}</span>
                    </div>
                    <h2 className="mt-4 text-base font-bold text-white leading-relaxed group-hover:text-[#a78bfa] transition-colors">
                      {item.title}
                    </h2>
                    <p className="mt-3 text-sm text-[#a0a0b0] leading-relaxed line-clamp-3">{item.excerpt}</p>
                    <div className="mt-auto pt-4 flex items-center gap-2 text-sm text-[#7a7a8a]">
                      <i className="ri-user-line"></i>
                      <span>{item.author}</span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}